import React, { useState, useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import Modal from 'components/Modal';
import ReactSelect from 'components/Select/ReactSelect';
import Button from 'components/Button';
import MessageBox from 'components/MessageBox';
// endpoints
import { getFarmsForFeedOrders, getFarmGroupsForFeedOrders } from 'endpoints/feedOrders';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { withRouter } from 'react-router';
// styles
import './CreateFeedOrderModal.scss';

const CreateFeedOrderModal = ({ router, closeModal, preSelectedFarm = null }) => {
  const { formatMessage } = useIntl();
  const [farms, setFarms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedFarmId, setSelectedFarmId] = useState(preSelectedFarm?.id || '');
  const [selectedFarmObject, setSelectedFarmObject] = useState(preSelectedFarm || null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [isLoadingFarms, setIsLoadingFarms] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [farmSearch, setFarmSearch] = useState('');
  const [farmPage, setFarmPage] = useState(1);
  const [hasMoreFarms, setHasMoreFarms] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [hasMoreGroups, setHasMoreGroups] = useState(true);

  const fetchFarms = async (search = '', page = 1, append = false) => {
    setIsLoadingFarms(true);
    try {
      const params = {
        page,
        per_page: 25,
        ...(search && { search })
      };
      const response = await getFarmsForFeedOrders(params);
      const newFarms = response.resources || [];

      if (append) {
        setFarms((prev) => [...prev, ...newFarms]);
      } else {
        setFarms(newFarms);
      }

      // Check if there are more farms to load
      const total = response.meta?.total || 0;
      const currentCount = append ? farms.length + newFarms.length : newFarms.length;
      setHasMoreFarms(currentCount < total);

    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsLoadingFarms(false);
    }
  };

  const fetchGroups = async (farmId, search = '', page = 1, append = false) => {
    if (!farmId) {
      setGroups([]);
      return;
    }

    setIsLoadingGroups(true);
    try {
      const params = {
        page,
        per_page: 25,
        ...(search && { search })
      };
      const response = await getFarmGroupsForFeedOrders(farmId, params);
      const newGroups = response.resources || [];

      if (append) {
        setGroups((prev) => [...prev, ...newGroups]);
      } else {
        setGroups(newGroups);
      }

      // Check if there are more groups to load
      const total = response.meta?.total || 0;
      const currentCount = append ? groups.length + newGroups.length : newGroups.length;
      setHasMoreGroups(currentCount < total);

    } catch (error) {
      toastResponseErrors(error);
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (preSelectedFarm) {
      // If farm is pre-selected, set it and fetch its groups
      setFarms([preSelectedFarm]);
      fetchGroups(preSelectedFarm.id);
    } else {
      // Otherwise fetch initial farms
      fetchFarms();
    }
  }, [preSelectedFarm]);

  const handleFarmSelection = (option) => {
    // Only allow farm selection if no pre-selected farm
    if (preSelectedFarm) return;

    const farmId = option ? option.value : '';
    setSelectedFarmId(farmId);

    if (option) {
      setSelectedFarmObject({ id: option.value, name: option.label });
    } else {
      setSelectedFarmObject(null);
    }

    setSelectedGroupId(''); // Reset group selection
    setGroupSearch(''); // Reset group search
    setGroupPage(1); // Reset group page

    if (farmId) {
      fetchGroups(farmId);
    } else {
      setGroups([]);
    }
  };

  const handleGroupSelection = (option) => {
    const groupId = option ? option.value : '';
    setSelectedGroupId(groupId);
  };

  const handleGroupSearchChange = (inputValue) => {
    setGroupSearch(inputValue);
    setGroupPage(1); // Reset to first page when searching
    fetchGroups(selectedFarmId, inputValue, 1, false);
  };

  const handleGroupMenuScrollToBottom = () => {
    if (!isLoadingGroups && hasMoreGroups) {
      const nextPage = groupPage + 1;
      setGroupPage(nextPage);
      fetchGroups(selectedFarmId, groupSearch, nextPage, true);
    }
  };

  const handleFarmSearchChange = (inputValue) => {
    setFarmSearch(inputValue);
    setFarmPage(1); // Reset to first page when searching
    fetchFarms(inputValue, 1, false);
  };

  const handleFarmMenuScrollToBottom = () => {
    if (!isLoadingFarms && hasMoreFarms) {
      const nextPage = farmPage + 1;
      setFarmPage(nextPage);
      fetchFarms(farmSearch, nextPage, true);
    }
  };

  const handleCreateFeedOrder = () => {
    if (selectedFarmId && selectedGroupId) {
      closeModal();
      const createUrl = `/feed-orders-farms/${selectedFarmId}/groups/${selectedGroupId}/feed-orders/create`;
      router.push(createUrl);
    }
  };

  const farmOptions = farms.map((farm) => ({
    value: farm.id,
    label: farm.name
  }));

  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: group.name
  }));

  // Use the stored selectedFarmObject or find it in farmOptions as fallback
  const selectedFarm = selectedFarmObject
    ? { value: selectedFarmObject.id, label: selectedFarmObject.name }
    : farmOptions.find((option) => option.value === selectedFarmId);
  const selectedGroup = groupOptions.find((option) => option.value === selectedGroupId);

  const isCreateDisabled = !selectedFarmId || !selectedGroupId;
  const hasGroups = groups.length > 0;

  const getGroupPlaceholder = () => {
    if (isLoadingGroups) return formatMessage({ id: 'general.loadingGroups' });
    if (!selectedFarmId) return formatMessage({ id: 'general.selectFarmFirst' });
    if (!hasGroups) return formatMessage({ id: 'general.farmHasNoGroups' });
    return formatMessage({ id: 'general.placeholder.searchForGroup' });
  };

  return (
    <Modal title={<FormattedMessage id="general.createFeedOrder" />} className="CreateFeedOrderModal">
      <section className="modal-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="form-field">
            <label>
              <FormattedMessage id="general.farm" />
              {preSelectedFarm && (
                <span style={{ marginLeft: '4px', fontSize: '12px', color: '#666' }}>
                  (Selected)
                </span>
              )}
            </label>
            <ReactSelect
              value={selectedFarm}
              onChange={handleFarmSelection}
              options={farmOptions}
              placeholder={
                preSelectedFarm ? preSelectedFarm.name : formatMessage({ id: 'general.placeholder.searchForFarm' })
              }
              searchable={!preSelectedFarm}
              clearable={!preSelectedFarm}
              disabled={!!preSelectedFarm}
              isLoading={isLoadingFarms}
              noResultsText={
                isLoadingFarms
                  ? formatMessage({ id: 'general.loading' })
                  : formatMessage({ id: 'general.noFarmsFound' })
              }
              onInputChange={handleFarmSearchChange}
              onMenuScrollToBottom={handleFarmMenuScrollToBottom}
              filterOption={() => true} // Disable client-side filtering since we're doing server-side search
            />
          </div>

          <div className="form-field">
            <label>
              <FormattedMessage id="general.group" />
            </label>
            <ReactSelect
              value={selectedGroup}
              onChange={handleGroupSelection}
              options={groupOptions}
              placeholder={getGroupPlaceholder()}
              searchable={selectedFarmId && hasGroups}
              clearable={false}
              disabled={!selectedFarmId}
              isLoading={isLoadingGroups}
              noResultsText={
                isLoadingGroups
                  ? formatMessage({ id: 'general.loading' })
                  : formatMessage({ id: 'general.noGroupsFound' })
              }
              onInputChange={handleGroupSearchChange}
              onMenuScrollToBottom={handleGroupMenuScrollToBottom}
              filterOption={() => true} // Disable client-side filtering since we're doing server-side search
            />
            {selectedFarmId && !isLoadingGroups && !hasGroups && (
              <MessageBox type="warning" className="mt-10">
                <FormattedMessage id="general.farmHasNoGroupsForFeedOrders" />
              </MessageBox>
            )}
          </div>
        </div>
      </section>

      <section className="modal-footer text-right">
        <Button className="margin-right-1" onClick={closeModal}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button
          className="primary"
          onClick={handleCreateFeedOrder}
          disabled={isCreateDisabled}
        >
          <FormattedMessage id="general.createFeedOrder" />
        </Button>
      </section>
    </Modal>
  );
};

CreateFeedOrderModal.propTypes = {
  router: T.object.isRequired,
  closeModal: T.func.isRequired,
  preSelectedFarm: T.shape({
    id: T.oneOfType([T.string, T.number]).isRequired,
    name: T.string.isRequired,
  }),
};

export default withRouter(connect(null, { closeModal })(CreateFeedOrderModal));
